import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { usePageSEO } from "@/lib/seo";
import { PROGRAMS } from "@/lib/programs";

export default function WatWeDoenPage() {
  usePageSEO({
    title: "Wat we doen | Stichting FIN",
    description:
      "Vier programma's van Stichting FIN: FIN Herstel, FIN Bestaanszekerheid, FIN Groei & Kansen en FIN Community. Eén missie, verschillende manieren om verschil te maken.",
  });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location.hash]);

  return (
    <div data-testid="wat-we-doen-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Wat we doen</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Eén missie. <em className="italic text-gold">Vier programma's</em>.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Onze projecten en programma's verschillen, maar hebben één ding gemeen: mensen en gemeenschappen sterker
            achterlaten dan we ze aantroffen. Bekijk wat FIN doet per programma.
          </p>
        </div>
      </section>

      {/* Programma-anchor navigation */}
      <section className="border-y border-line bg-cream sticky top-[76px] md:top-[84px] z-30 backdrop-blur">
        <div className="container-x py-4 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol shrink-0 mr-1">Ga naar</p>
          {PROGRAMS.map((p) => (
            <a
              key={p.key}
              href={`#${p.anchorId}`}
              className="text-[13px] rounded-full px-3.5 py-1.5 border border-line text-inkMuted hover:border-gold hover:text-petrol transition whitespace-nowrap"
              data-testid={`anchor-${p.key}`}
            >
              {p.label}
            </a>
          ))}
        </div>
      </section>

      {/* Programma-secties */}
      {PROGRAMS.map((p, i) => {
        const Icon = p.icon;
        const flipped = i % 2 === 1;
        return (
          <section
            key={p.key}
            id={p.anchorId}
            className={`py-20 md:py-28 ${i % 2 === 0 ? "bg-cream" : "bg-sand"} scroll-mt-32`}
            data-testid={`section-${p.key}`}
          >
            <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
              <div className={`lg:col-span-6 ${flipped ? "lg:order-2" : ""}`}>
                <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden shadow-card">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-petrol/40 via-transparent to-transparent" />
                  <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-cream/95 backdrop-blur rounded-full px-4 py-2">
                    <Icon size={16} className="text-petrol" strokeWidth={2} />
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol">{p.label}</span>
                  </div>
                </div>
              </div>
              <div className={`lg:col-span-6 ${flipped ? "lg:order-1" : ""}`}>
                <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Programma {String(i + 1).padStart(2, "0")}</p>
                <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol mb-4">
                  {p.subtitle}
                </h2>
                <p className="text-[17px] leading-[1.75] text-inkMuted text-pretty mb-6">{p.long}</p>
                <ul className="space-y-2.5 mb-8">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-[15.5px] text-petrol">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-goldSoft text-petrol shrink-0 mt-0.5">
                        <Check size={13} strokeWidth={3} />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/projecten?thema=${p.key}`}
                    className="inline-flex items-center gap-2 bg-petrol text-cream hover:bg-petrolDark transition rounded-full px-6 py-3 text-sm font-semibold"
                    data-testid={`section-cta-projecten-${p.key}`}
                  >
                    Projecten in dit programma <ArrowRight size={15} />
                  </Link>
                  <Link
                    to="/hulp-nodig"
                    className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold"
                    data-testid={`section-cta-hulp-${p.key}`}
                  >
                    Ik zoek hulp <ArrowUpRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-20 md:py-24">
        <div className="container-x">
          <div className="bg-petrol text-cream rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/20 blur-3xl" />
            <div className="relative max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Samenwerken?</p>
              <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-balance mb-4">
                Herkent u een programma dat aansluit bij uw vraag?
              </h2>
              <p className="text-cream/85 leading-relaxed max-w-2xl">
                Wij denken graag mee — over doorverwijzing, projectpartnerschap of financiering. Één gesprek geeft meestal
                voldoende richting.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/voor-gemeenten"
                  className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold shadow-gold"
                >
                  Voor gemeenten & partners <ArrowUpRight size={15} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-6 py-3 text-sm font-semibold"
                >
                  Plan een kennismaking
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
